
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useToast } from '@/hooks/use-toast';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }: { error?: Error }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    console.error(`Error occurred at path: ${location.pathname}`, error);
    
    // Log to console in development, could be replaced with proper error tracking service
    toast({
      variant: "destructive",
      title: "Something went wrong",
      description: "An error occurred. Our team has been notified.",
    });
  }, [error, location.pathname]);

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center p-4">
      <h2 className="text-2xl font-semibold mb-4">Something went wrong</h2>
      <p className="text-muted-foreground mb-6 text-center">
        We've been notified and are working to fix the issue.
      </p>
      <div className="flex gap-4">
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
        >
          Go Back
        </Button>
        <Button
          onClick={() => navigate('/', { replace: true })}
        >
          Return Home
        </Button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
